// pages/Connect.js
import React, { useState, useEffect, useRef } from "react";
import UserInfoOverlay from "../components/UserInfoOverlay";
export default function Connect({
  users = [],
  searchUsers,
  myChatId,
  onFollow,
  onUnfollow,
  onBack,
  uploadImage,
  socket,
  groups = [],
  groupMembers = {},
  onOpenGroup,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  const searchRequestRef = useRef(0);

  /* 👤 Selected user for the info overlay (click name/avatar → open, click Follow → don't) */
  const [selectedUserId, setSelectedUserId] = useState(null);

  /* =========================================================
     🔎 SEARCH USERS

     - Empty search → normal nearby users remain unchanged
     - 1+ characters → HTTP user search
     - Backend searches username + displayName
     - Returns up to 20 matching users
  ========================================================= */
  useEffect(() => {
    const query = searchQuery.trim();

    /* No search → return to normal nearby users */
    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError("");
      return;
    }

    /*
     * Search is allowed from the first character.
     *
     * Example:
     * R → users beginning with R
     * Ra → users beginning with Ra
     * Rob → users beginning with Rob
     */
    if (query.length < 1) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError("");
      return;
    }

    const requestId = ++searchRequestRef.current;

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        setSearchError("");

        const data = await searchUsers?.({
          q: query,
          limit: 20,
        });

        /* Ignore an older request if a newer search completed */
        if (requestId !== searchRequestRef.current) return;

        setSearchResults(
          Array.isArray(data?.users) ? data.users : []
        );
      } catch (err) {
        if (requestId !== searchRequestRef.current) return;

        console.error("🔥 [CONNECT USER SEARCH ERROR]", err);

        setSearchResults([]);
        setSearchError("Failed to search users");
      } finally {
        if (requestId === searchRequestRef.current) {
          setSearchLoading(false);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  /* =========================================================
     🔄 KEEP SEARCH RESULTS IN SYNC WITH FOLLOW / UNFOLLOW

     GroupChat only updates its own users/nearbyUsers lists.
     searchResults is local to this page, so we update it here
     from the same server confirmations.
  ========================================================= */
  useEffect(() => {
    if (!socket) return;

    const patch = (targetChatId, isFollowing, followersCount) => {
      setSearchResults((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(targetChatId)
            ? {
                ...u,
                isFollowing,
                followersCount: followersCount ?? u.followersCount,
              }
            : u
        )
      );
    };

    const onFollowSuccess = ({ targetChatId, followersCount }) =>
      patch(targetChatId, true, followersCount);

    const onUnfollowSuccess = ({ targetChatId, followersCount }) =>
      patch(targetChatId, false, followersCount);

    socket.on("feed-follow-success", onFollowSuccess);
    socket.on("feed-unfollow-success", onUnfollowSuccess);

    return () => {
      socket.off("feed-follow-success", onFollowSuccess);
      socket.off("feed-unfollow-success", onUnfollowSuccess);
    };
  }, [socket]);

  /* =========================================================
     👥 NORMAL NEARBY USERS

     IMPORTANT:
     This remains unchanged when there is no search query.
  ========================================================= */
  const displayedUsers = searchQuery.trim()
    ? searchResults
    : users;

    /* 👤 Resolve the selected user fresh from the current list each render,
     so the overlay's follow state stays in sync after a follow/unfollow. */
  const selectedUser = displayedUsers.find(
    (u) => String(u.chatId) === String(selectedUserId)
  );

  /* 👥 Groups selectedUser and I both belong to — same logic ChatWindow
     uses for the chat-header "User Info" overlay. */
  const mutualGroups = selectedUser
    ? (groups || []).filter((g) => {
        const mem = groupMembers?.[g.groupId] || g.members || [];
        return (
          Array.isArray(mem) &&
          mem.length > 0 &&
          mem.some((m) => String(m.chatId) === String(myChatId)) &&
          mem.some((m) => String(m.chatId) === String(selectedUser.chatId))
        );
      })
    : [];

  return (
    <div
      className="flex flex-col w-full bg-white dark:bg-neutral-900"
      style={{ height: "100%", overflow: "hidden" }}
    >
     {/* HEADER */}
<div
  className="flex-shrink-0 flex items-center gap-2 px-2 py-3 border-b border-neutral-200 dark:border-neutral-800"
  style={{
    paddingTop: "max(12px, env(safe-area-inset-top, 12px))",
  }}
>
  <button
    onClick={onBack}
    className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 transition-colors"
    aria-label="Back"
  >
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  </button>

  <h2 className="text-base sm:text-lg font-semibold">
    Connect
  </h2>
</div>

      {/* SEARCH BAR */}
      <div className="flex-shrink-0 px-3 sm:px-4 py-2 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800 rounded-full px-3.5 py-2">
          <span className="text-neutral-400 text-sm">
            🔍
          </span>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search people"
            className="flex-1 bg-transparent outline-none text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400"
          />

          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="text-neutral-400 text-sm px-1 touch-manipulation active:opacity-60"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* USER LIST */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3 space-y-2"
        style={{
          WebkitOverflowScrolling: "touch",
          paddingBottom: "env(safe-area-inset-bottom, 16px)",
        }}
      >
        {/* SEARCH LOADING */}
        {searchLoading && (
          <div className="flex flex-col items-center justify-center h-40 text-center text-neutral-500">
            <div className="text-2xl mb-3 animate-pulse">
              🔍
            </div>

            <div className="text-sm">
              Searching...
            </div>
          </div>
        )}

        {/* SEARCH ERROR */}
        {!searchLoading && searchError && (
          <div className="flex flex-col items-center justify-center h-40 text-center text-neutral-500">
            <div className="text-4xl mb-3">
              ⚠️
            </div>

            <div className="text-sm">
              {searchError}
            </div>
          </div>
        )}

        {/* NO SEARCH RESULTS */}
        {!searchLoading &&
          !searchError &&
          searchQuery.trim().length >= 1 &&
          searchResults.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-center text-neutral-500">
              <div className="text-4xl mb-3">
                👥
              </div>

              <div className="text-sm">
                No matching users
              </div>
            </div>
          )}

        {/* NORMAL NEARBY USERS */}
        {!searchQuery.trim() &&
          displayedUsers.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-center text-neutral-500">
              <div className="text-4xl mb-3">
                👥
              </div>

              <div className="text-sm">
                No users found
              </div>
            </div>
          )}

        {/* USERS */}
        {!searchLoading &&
          !searchError &&
          displayedUsers.map((u) => {
            if (String(u.chatId) === String(myChatId)) {
              return null;
            }

            return (
              <div
                key={u.chatId}
                className="flex items-center justify-between rounded-xl p-3 sm:p-3.5 bg-neutral-50 dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 touch-manipulation"
              >
                {/* LEFT — only avatar + name/username opens the overlay */}
                <div
                  onClick={() => setSelectedUserId(u.chatId)}
                  className="flex items-center gap-3 min-w-0 cursor-pointer active:opacity-80"
                >
                  {u.profileImageUrl ? (
                    <img
                      src={u.profileImageUrl}
                      alt={u.username || u.displayName || "User"}
                      className="w-11 h-11 sm:w-10 sm:h-10 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-neutral-300 dark:bg-neutral-600 flex items-center justify-center shrink-0 text-lg">
                      👤
                    </div>
                  )}

                  <div className="min-w-0">
                    {u.displayName ? (
                      <>
                        <div className="font-medium text-sm truncate">
                          {u.displayName}
                        </div>

                        <div className="text-xs text-neutral-500 truncate">
                          @{u.username}
                        </div>
                      </>
                    ) : (
                      <div className="font-medium text-sm truncate">
                        @{u.username}
                      </div>
                    )}

                    <div className="text-xs text-neutral-500 mt-0.5">
                      {u.followersCount ?? 0} followers ·{" "}
                      {u.followingCount ?? 0} following
                    </div>
                  </div>
                </div>

                {/* FOLLOW BUTTON — outside the clickable left block, so it never opens the overlay */}
                {u.isFollowing ? (
                  <button
                    onClick={() => onUnfollow?.(u.chatId)}
                    className="ml-3 shrink-0 px-3.5 py-2 sm:py-1.5 rounded-full text-sm bg-neutral-200 dark:bg-neutral-600 text-neutral-800 dark:text-neutral-200 touch-manipulation active:opacity-70 font-medium"
                  >
                    Following
                  </button>
                ) : (
                  <button
                    onClick={() => onFollow?.(u.chatId)}
                    className="ml-3 shrink-0 px-3.5 py-2 sm:py-1.5 rounded-full text-sm bg-green-600 text-white touch-manipulation active:bg-green-700 font-medium"
                  >
                    Follow
                  </button>
                )}
              </div>
            );
          })}
      </div>

      {/* USER INFO OVERLAY */}
    <UserInfoOverlay
  open={!!selectedUser}
  onClose={() => setSelectedUserId(null)}
  user={selectedUser}
  initialTab="groups"
  mutualGroups={mutualGroups}
  onOpenGroup={onOpenGroup}
  myChatId={myChatId}
  uploadImage={uploadImage}
  socket={socket}
  isFollowing={!!selectedUser?.isFollowing}
  followersCount={selectedUser?.followersCount ?? 0}
  followingCount={selectedUser?.followingCount ?? 0}
  onFollow={() => onFollow?.(selectedUser?.chatId)}
  onUnfollow={() => onUnfollow?.(selectedUser?.chatId)}
/>
    </div>
  );
}